/**
 * Typed query / mutation hooks for `/api/bi/crm/*` endpoints.
 *
 * Mirrors the conventions of `src/api/bi/hooks.ts`:
 *   - read hooks are `useQuery` wrappers,
 *   - write hooks are `useMutation` with `onSuccess` invalidation,
 *   - file uploads go through axios `apiClient` directly with FormData.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient, get, post } from '../client';
import { svcPost } from '../svc/client';

// ---------------------------------------------------------------------------
// Types — mirror the JSON returned by the Django serializers in
// `src/crm/serializers.py`. Decimal fields come back as strings.
// ---------------------------------------------------------------------------

export type CrmTicketType =
  | 'sla_breach'
  | 'customer_delay'
  | 'cancellation'
  | 'refund'
  | 'shrinkage'
  | 'damage'
  | 'channel_escalation';

export type CrmTicketStatus =
  | 'open'
  | 'investigating'
  | 'waiting_customer'
  | 'escalated'
  | 'resolved'
  | 'rejected';

export type CrmTicketPriority = 'low' | 'normal' | 'high' | 'critical';

export type CrmReasonCategory =
  | 'sla_breach'
  | 'customer_delay'
  | 'cancellation'
  | 'refund'
  | 'shrinkage'
  | 'damage';

export type CrmAudience =
  | 'packer'
  | 'biller'
  | 'rider'
  | 'customer'
  | 'store_manager'
  | 'internal';

export type CrmRefundDecision = 'pending' | 'approved' | 'rejected';

export type CrmShrinkageStatus = 'reported' | 'verified' | 'approved' | 'rejected';

export type CrmShrinkageItemReason =
  | 'theft'
  | 'misplaced'
  | 'expired'
  | 'damaged'
  | 'unknown';

export interface CrmUserRef {
  id: number;
  name: string;
  phone: string | null;
  role: string | null;
}

export type CrmAvoidableHint = 'yes' | 'no' | 'unknown' | '';

export interface CrmReason {
  id: number;
  code: string;
  label: string;
  category: CrmReasonCategory;
  parent_id: number | null;
  is_active: boolean;
  order: number;
  /** Default suggestion for the ticket-level Is_Avoidable selector. */
  is_avoidable_hint: CrmAvoidableHint;
}

export type CrmTicketAvoidable = 'yes' | 'no' | 'unknown' | '';

export interface CrmAssignment {
  id: number;
  ticket_id: number;
  user: CrmUserRef | null;
  assigned_by: CrmUserRef | null;
  is_primary: boolean;
  note: string;
  acknowledged_at: string | null;
  created_at: string;
}

export interface CrmInteraction {
  id: number;
  ticket_id: number;
  audience: CrmAudience;
  actor_user: CrmUserRef | null;
  contacted_user: CrmUserRef | null;
  contacted_name: string;
  contacted_phone: string;
  note: string;
  is_outgoing: boolean;
  called_at: string | null;
  duration_seconds: number | null;
  created_at: string;
  call_recording_url: string | null;
  call_recording_meta: {
    id: number;
    duration: number | null;
    size: number | null;
    called_at: string | null;
    is_outgoing: boolean;
  } | null;
}

export interface CrmRefundItem {
  id: number;
  order_item_id: number;
  product_name: string | null;
  quantity: number;
  quantity_in_order: number | null;
  unit_price_snapshot: string;
  line_amount: string;
  reason_code: CrmReason | null;
  note: string;
}

export type CrmSettlementStatus = 'pending' | 'completed' | 'cancelled';

export interface CrmRefundSettlement {
  id: number;
  status: CrmSettlementStatus;
  refund_type: 'full' | 'partial';
  refund_amount: string;
  created_at: string | null;
  completed_at: string | null;
  remarks: string;
  image: string | null;
  /**
   * Null for refunds settled from the Django HTML screens, which record the
   * status but not who recorded it.
   */
  settled_by: CrmUserRef | null;
}

/**
 * How the settlement was found:
 *   - `linked`           the `CRMRefundRequest.settlement` FK was set
 *   - `matched_by_order` derived from `payment.Refund.order_id`, which is the
 *                        only way to see refunds settled from the HTML screens
 */
export type CrmSettlementSource = 'linked' | 'matched_by_order';

export interface CrmRefund {
  id: number;
  ticket_id: number;
  order_id: number | null;
  customer: CrmUserRef | null;
  requested_amount: string;
  approved_amount: string | null;
  payment_type_at_order: string;
  customer_reason: string;
  internal_notes: string;
  decision: CrmRefundDecision;
  decided_by: CrmUserRef | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  items: CrmRefundItem[];
  /** Accounts settlement, by FK when linked and by order id otherwise. */
  settlement?: CrmRefundSettlement | null;
  settlement_source?: CrmSettlementSource | null;
}

export interface CrmShrinkageItem {
  id: number;
  product_id: number;
  product_name: string | null;
  quantity: number;
  reason: CrmShrinkageItemReason;
  estimated_unit_cost: string;
  estimated_cost: string;
  attached_order_id: number | null;
  note: string;
}

export interface CrmShrinkage {
  id: number;
  ticket_id: number;
  store_id: number | null;
  store_name: string | null;
  discovered_at: string | null;
  discovery_source: 'support_call' | 'dayclose_variance' | 'audit' | 'inventory_count';
  total_estimated_loss: string;
  summary: string;
  verification_status: CrmShrinkageStatus;
  verified_by: CrmUserRef | null;
  verified_at: string | null;
  approved_by: CrmUserRef | null;
  approved_at: string | null;
  rejected_reason: string;
  created_at: string;
  updated_at: string;
  items: CrmShrinkageItem[];
}

export interface CrmBreachTag {
  id: number;
  tag: string;
  late_duration: number | null;
  created_at: string;
}

export interface CrmOrderSummary {
  id: number;
  status: string;
  store_id: number | null;
  store_name: string | null;
  final_total: string;
  payment_type: string | null;
  is_payment_done: boolean;
  created_timestamp: string | null;
  packed_timestamp: string | null;
  transaction_code: string | null;
  billed_timestamp: string | null;
  picked_timestamp: string | null;
  reached_timestamp: string | null;
  completed_timestamp: string | null;
  /** Rider back-to-store (same source as staff `return_timestamp`). */
  return_timestamp?: string | null;
  cancelled_timestamp: string | null;
  cancellation_reason: string | null;
  customer: { id: number | null; name: string | null; phone: string | null; address: string | null } | null;
  packer: { id: number | null; name: string | null; phone: string | null; address: string | null } | null;
  biller: { id: number | null; name: string | null; phone: string | null; address: string | null } | null;
  rider: { id: number | null; name: string | null; phone: string | null; address: string | null } | null;
}

export interface CrmCycle {
  id: number,
  identifier: string,
  status: string,
  is_operational: boolean
}
export interface CrmCycleDamageSummary {

  id: number,
  cycle: CrmCycle
  store_id: number,
  store_name: string,
  damage_reason: string,
  damaged_at: string | null,
  damaged_image: string | null;
  reported_by: {
    id: number,
    name: string,
    phone: string | null,
    role: string
  },
  repaired_at: string | null,
  repaired_note: string | null
  repaired_image?: string | null
}

export interface CrmTicket {
  id: number;
  type: CrmTicketType;
  status: CrmTicketStatus;
  priority: CrmTicketPriority;
  summary: string;
  resolution_note: string;
  is_auto_created: boolean;
  order_id: number | null;
  store_id: number | null;
  store_name: string | null;
  customer: CrmUserRef | null;
  breach_tag: CrmBreachTag | null;
  reason_code: CrmReason | null;
  sub_reason_code: CrmReason | null;
  /** Top-level "Category" tier of the SLA disposition cascade. */
  reason_root: CrmReason | null;
  is_avoidable: CrmTicketAvoidable;
  sla_disposition_notes: string;
  created_by: CrmUserRef | null;
  resolved_by: CrmUserRef | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  // List rows include these as numbers; detail responses fill in the
  // arrays below.
  assignment_count: number | null;
  interaction_count: number | null;
  assignments?: CrmAssignment[];
  interactions?: CrmInteraction[];
  order?: CrmOrderSummary | null;
  refund?: CrmRefund | null;
  shrinkage?: CrmShrinkage | null;
  // Cycle Damage
  cycle_damages?: CrmCycleDamageSummary[];
  repaired_image?: string | null;
}

export interface PaginatedCrmTickets {
  results: CrmTicket[];
  pagination: { page: number; page_size: number; total: number; total_pages: number };
  filters: Record<string, string | number | null>;
}

export interface PaginatedCrmRefunds {
  results: CrmRefund[];
  pagination: { page: number; page_size: number; total: number; total_pages: number };
}

export interface PaginatedCrmShrinkage {
  results: CrmShrinkage[];
  pagination: { page: number; page_size: number; total: number; total_pages: number };
}

export interface CrmAssigneesResponse {
  results: CrmUserRef[];
  pagination: { page: number; page_size: number; total: number; total_pages: number };
  roles: string[];
}

export interface CrmReasonsResponse {
  results: CrmReason[];
}

// ---------------------------------------------------------------------------
// SLA disposition analytics
// ---------------------------------------------------------------------------

export interface SlaDispositionFilters {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
  isAvoidable?: 'yes' | 'no' | 'unknown' | null;
  category?: string | null;
}

export interface SlaDispositionAggregate {
  range: { start: string; end: string };
  total_tickets: number;
  by_category: Array<{
    root_id: number;
    root_code: string;
    root_label: string;
    count: number;
  }>;
  by_reason: Array<{
    reason_id: number;
    reason_code: string;
    reason_label: string;
    root_code: string | null;
    root_label: string | null;
    count: number;
  }>;
  by_sub_reason: Array<{
    sub_reason_id: number;
    sub_reason_code: string;
    sub_reason_label: string;
    reason_code: string | null;
    reason_label: string | null;
    count: number;
  }>;
  by_avoidable: Record<string, number>;
  by_breach_tag: Array<{ tag: string; count: number; avg_late_seconds: number }>;
  by_store: Array<{
    store_id: number;
    store_name: string;
    count: number;
    avoidable: number;
  }>;
  trend: Array<{ day: string | null; count: number; avoidable: number }>;
}

export function useSlaDispositionAggregate(filters: SlaDispositionFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'crm',
      'analytics',
      'sla-disposition',
      filters.start ?? null,
      filters.end ?? null,
      filters.storeId ?? null,
      filters.isAvoidable ?? null,
      filters.category ?? null,
    ],
    queryFn: () =>
      get<SlaDispositionAggregate>('/api/bi/crm/analytics/sla-disposition/', {
        params: {
          start: filters.start ?? undefined,
          end: filters.end ?? undefined,
          store_id: filters.storeId ?? undefined,
          is_avoidable: filters.isAvoidable ?? undefined,
          category: filters.category ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export interface CrmTicketsFilters {
  type?: CrmTicketType | null;
  status?: CrmTicketStatus | null;
  priority?: CrmTicketPriority | null;
  storeId?: number | null;
  orderId?: number | null;
  assigneeId?: number | null;
  /**
   * Free-text search forwarded as `q`. Django matches summary / order /
   * customer name, and (once DROPIT_TICKET_PHONE_SEARCH is applied) phones
   * on the customer, linked order (snapshot + packer/biller/rider), and
   * ticket assignees.
   */
  q?: string | null;
  isOpen?: boolean | null;
  page?: number;
  pageSize?: number;
}

function ticketsParams(f: CrmTicketsFilters): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: f.page ?? 1,
    page_size: f.pageSize ?? 25,
  };
  if (f.type) out.type = f.type;
  if (f.status) out.status = f.status;
  if (f.priority) out.priority = f.priority;
  if (f.storeId != null) out.store_id = f.storeId;
  if (f.orderId != null) out.order_id = f.orderId;
  if (f.assigneeId != null) out.assignee_id = f.assigneeId;
  if (f.q && f.q.trim()) out.q = f.q.trim();
  if (f.isOpen) out.is_open = '1';
  return out;
}

// ---------------------------------------------------------------------------
// Read hooks
// ---------------------------------------------------------------------------

export function useCrmTickets(f: CrmTicketsFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'crm',
      'tickets',
      f.type ?? null,
      f.status ?? null,
      f.priority ?? null,
      f.storeId ?? null,
      f.orderId ?? null,
      f.assigneeId ?? null,
      f.q ?? '',
      f.isOpen ?? null,
      f.page ?? 1,
      f.pageSize ?? 25,
    ],
    queryFn: () =>
      get<PaginatedCrmTickets>('/api/bi/crm/tickets/', { params: ticketsParams(f) }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useCrmTicket(id: number | null | undefined) {
  return useQuery({
    queryKey: ['bi', 'crm', 'ticket', id ?? null],
    queryFn: () => get<CrmTicket>(`/api/bi/crm/tickets/${id}/`),
    enabled: id != null && id > 0,
    staleTime: 10_000,
  });
}

export function useCrmInbox(onlyUnack = true, enabled = true) {
  return useQuery({
    queryKey: ['bi', 'crm', 'inbox', onlyUnack],
    queryFn: () =>
      get<{ results: CrmTicket[] }>('/api/bi/crm/inbox/', {
        params: onlyUnack ? { only_unack: '1' } : {},
      }),
    enabled,
    refetchInterval: 30_000,
  });
}

export function useCrmAssignees(q: string, roles?: string[], pageSize = 100) {
  const size = Math.max(1, Math.min(pageSize, 100));
  return useQuery({
    queryKey: ['bi', 'crm', 'assignees', q, (roles ?? []).join(','), size],
    queryFn: () =>
      get<CrmAssigneesResponse>('/api/bi/crm/assignees/', {
        params: {
          q: q || undefined,
          roles: roles && roles.length > 0 ? roles.join(',') : undefined,
          page_size: size,
        },
      }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useCrmReasonCodes(category?: CrmReasonCategory) {
  return useQuery({
    queryKey: ['bi', 'crm', 'reason-codes', category ?? null],
    queryFn: () =>
      get<CrmReasonsResponse>('/api/bi/crm/reason-codes/', {
        params: category ? { category } : {},
      }),
    staleTime: 5 * 60_000,
  });
}

/** `unsettled` means no `payment.Refund` row exists for the order at all. */
export type CrmSettlementFilter =
  | CrmSettlementStatus
  | 'settled'
  | 'unsettled';

export function useCrmRefunds(filters: {
  decision?: CrmRefundDecision | null;
  settlementStatus?: CrmSettlementFilter | null;
  storeId?: number | null;
  orderId?: number | null;
  ticketId?: number | null;
  q?: string | null;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'crm',
      'refunds',
      filters.decision ?? null,
      filters.settlementStatus ?? null,
      filters.storeId ?? null,
      filters.orderId ?? null,
      filters.ticketId ?? null,
      filters.q ?? null,
      filters.page ?? 1,
      filters.pageSize ?? 25,
    ],
    queryFn: () =>
      get<PaginatedCrmRefunds>('/api/bi/crm/refunds/', {
        params: {
          page: filters.page ?? 1,
          page_size: filters.pageSize ?? 25,
          decision: filters.decision ?? undefined,
          settlement_status: filters.settlementStatus ?? undefined,
          store_id: filters.storeId ?? undefined,
          order_id: filters.orderId ?? undefined,
          ticket_id: filters.ticketId ?? undefined,
          q: filters.q ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });
}

export function useCrmShrinkage(filters: {
  status?: CrmShrinkageStatus | null;
  storeId?: number | null;
  ticketId?: number | null;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'crm',
      'shrinkage',
      filters.status ?? null,
      filters.storeId ?? null,
      filters.ticketId ?? null,
      filters.page ?? 1,
      filters.pageSize ?? 25,
    ],
    queryFn: () =>
      get<PaginatedCrmShrinkage>('/api/bi/crm/shrinkage/', {
        params: {
          page: filters.page ?? 1,
          page_size: filters.pageSize ?? 25,
          verification_status: filters.status ?? undefined,
          store_id: filters.storeId ?? undefined,
          ticket_id: filters.ticketId ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function invalidateCrm(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['bi', 'crm'] });
}

export interface CreateTicketBody {
  type: CrmTicketType;
  order_id?: number | null;
  store_id?: number | null;
  customer_id?: number | null;
  reason_code_id?: number | null;
  sub_reason_code_id?: number | null;
  priority?: CrmTicketPriority;
  summary?: string;
}

export function useCreateCrmTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTicketBody) => post<CrmTicket>('/api/bi/crm/tickets/', body),
    onSuccess: () => invalidateCrm(qc),
  });
}

export interface PatchTicketBody {
  status?: CrmTicketStatus;
  priority?: CrmTicketPriority;
  summary?: string;
  resolution_note?: string;
  reason_code_id?: number | null;
  sub_reason_code_id?: number | null;
  is_avoidable?: CrmTicketAvoidable;
  sla_disposition_notes?: string;
}

export function usePatchCrmTicket(id: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PatchTicketBody) => {
      if (id == null) throw new Error('Missing ticket id');
      const r = await apiClient.patch<CrmTicket>(`/api/bi/crm/tickets/${id}/`, body);
      return r.data;
    },
    onSuccess: () => invalidateCrm(qc),
  });
}

export interface PatchCycleDamageTicketBody {
  ticket_id: number;
  repaired_note: string;
  repaired_image: File | Blob;
}

export function usePostCrmCycleDamageTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PatchCycleDamageTicketBody) => {
      const fd = new FormData();
      fd.append('ticket_id', String(body.ticket_id));
      fd.append('repaired_note', body.repaired_note);
      if (body.repaired_image instanceof File) {
        fd.append('repaired_image', body.repaired_image, body.repaired_image.name || 'repaired.jpg');
      } else {
        fd.append('repaired_image', body.repaired_image, 'repaired.jpg');
      }
      return svcPost<CrmTicket>('/svc/crm/cycle-damage-resolve/', fd);
    },
    onSuccess: () => invalidateCrm(qc),
  });
}

export const usePatchCrmCycleDamageTicket = usePostCrmCycleDamageTicket;
export const useResolveCrmCycleDamageTicket = usePostCrmCycleDamageTicket;

export interface AssignTicketBody {
  user_ids: number[];
  primary?: number | null;
  note?: string;
}

export function useAssignCrmTicket(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignTicketBody) =>
      post<{ ticket_id: number; assignments: CrmAssignment[] }>(
        `/api/bi/crm/tickets/${id}/assign/`,
        body,
      ),
    onSuccess: () => invalidateCrm(qc),
  });
}

/**
 * Same endpoint as `useAssignCrmTicket`, but the ticket id travels with the
 * payload — needed when the id only exists after a create call resolves.
 */
export function useAssignCrmTicketById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, ...body }: AssignTicketBody & { ticketId: number }) =>
      post<{ ticket_id: number; assignments: CrmAssignment[] }>(
        `/api/bi/crm/tickets/${ticketId}/assign/`,
        body,
      ),
    onSuccess: () => invalidateCrm(qc),
  });
}

export interface EscalateTicketBody extends AssignTicketBody {
  escalation_note?: string;
}

export function useEscalateCrmTicket(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EscalateTicketBody) =>
      post<{ ticket: CrmTicket; assignments: CrmAssignment[] }>(
        `/api/bi/crm/tickets/${id}/escalate/`,
        body,
      ),
    onSuccess: () => invalidateCrm(qc),
  });
}

export interface CreateInteractionInput {
  audience: CrmAudience;
  note?: string;
  file?: File | null;
  contacted_user_id?: number | null;
  contacted_name?: string;
  contacted_phone?: string;
  called_at?: string | null;
  duration_seconds?: number | null;
  is_outgoing?: boolean;
  /**
   * Upload progress 0–100. Recordings come off phones over mobile data, so
   * the composer shows a real bar instead of an indeterminate "Saving…".
   */
  onProgress?: (percent: number) => void;
}

export function useCreateCrmInteraction(ticketId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateInteractionInput) => {
      const fd = new FormData();
      fd.append('audience', body.audience);
      if (body.note) fd.append('note', body.note);
      if (body.file) fd.append('file', body.file);
      if (body.contacted_user_id != null)
        fd.append('contacted_user_id', String(body.contacted_user_id));
      if (body.contacted_name) fd.append('contacted_name', body.contacted_name);
      if (body.contacted_phone) fd.append('contacted_phone', body.contacted_phone);
      if (body.called_at) fd.append('called_at', body.called_at);
      if (body.duration_seconds != null)
        fd.append('duration_seconds', String(body.duration_seconds));
      if (body.is_outgoing != null) fd.append('is_outgoing', body.is_outgoing ? 'true' : 'false');

      const r = await apiClient.post<CrmInteraction>(
        `/api/bi/crm/tickets/${ticketId}/interactions/`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: body.onProgress
            ? (event) => {
              if (!event.total) return;
              body.onProgress?.(Math.round((event.loaded / event.total) * 100));
            }
            : undefined,
        },
      );
      return r.data;
    },
    onSuccess: () => invalidateCrm(qc),
  });
}

export function useAcknowledgeInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ticketIds: number[]) =>
      post<{ acknowledged: number }>('/api/bi/crm/inbox/', { ticket_ids: ticketIds }),
    onSuccess: () => invalidateCrm(qc),
  });
}

// ---------------------------------------------------------------------------
// Refund mutations
// ---------------------------------------------------------------------------

export interface RefundItemInput {
  order_item_id?: number;
  quantity: number;
  unit_price_snapshot?: string | number;
  line_amount?: string | number;
  reason_code_id?: number | null;
  note?: string;
}

export interface CreateRefundBody {
  ticket_id: number;
  requested_amount: string | number;
  customer_reason?: string;
  internal_notes?: string;
  items?: RefundItemInput[];
}

export function useCreateCrmRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRefundBody) => post<CrmRefund>('/api/bi/crm/refunds/', body),
    onSuccess: () => invalidateCrm(qc),
  });
}

export interface PatchRefundBody {
  requested_amount?: string | number;
  customer_reason?: string;
  internal_notes?: string;
  items?: RefundItemInput[];
}

export function usePatchCrmRefund(id: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PatchRefundBody) => {
      if (id == null) throw new Error('Missing refund id');
      const r = await apiClient.patch<CrmRefund>(`/api/bi/crm/refunds/${id}/`, body);
      return r.data;
    },
    onSuccess: () => invalidateCrm(qc),
  });
}

export function useDecideCrmRefund(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      decision: 'approved' | 'rejected';
      approved_amount?: string | number;
      note?: string;
    }) => post<CrmRefund>(`/api/bi/crm/refunds/${id}/decision/`, body),
    onSuccess: () => invalidateCrm(qc),
  });
}

// ---------------------------------------------------------------------------
// Shrinkage mutations
// ---------------------------------------------------------------------------

export interface ShrinkageItemInput {
  product_id: number;
  quantity: number;
  reason: CrmShrinkageItemReason;
  estimated_unit_cost?: string | number;
  estimated_cost?: string | number;
  attached_order_id?: number | null;
  note?: string;
}

export interface CreateShrinkageBody {
  ticket_id: number;
  store_id?: number | null;
  discovered_at?: string;
  discovery_source?: 'support_call' | 'dayclose_variance' | 'audit' | 'inventory_count';
  summary?: string;
  items?: ShrinkageItemInput[];
}

export function useCreateCrmShrinkage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateShrinkageBody) =>
      post<CrmShrinkage>('/api/bi/crm/shrinkage/', body),
    onSuccess: () => invalidateCrm(qc),
  });
}

export function usePatchCrmShrinkage(id: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      summary?: string;
      discovery_source?: CreateShrinkageBody['discovery_source'];
      items?: ShrinkageItemInput[];
    }) => {
      if (id == null) throw new Error('Missing shrinkage id');
      const r = await apiClient.patch<CrmShrinkage>(`/api/bi/crm/shrinkage/${id}/`, body);
      return r.data;
    },
    onSuccess: () => invalidateCrm(qc),
  });
}

export function useVerifyCrmShrinkage(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<CrmShrinkage>(`/api/bi/crm/shrinkage/${id}/verify/`, {}),
    onSuccess: () => invalidateCrm(qc),
  });
}

export function useApproveCrmShrinkage(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { decision: 'approved' | 'rejected'; rejected_reason?: string }) =>
      post<CrmShrinkage>(`/api/bi/crm/shrinkage/${id}/approve/`, body),
    onSuccess: () => invalidateCrm(qc),
  });
}
